import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import csi from "csi/main";
import Request from "csi/comm/Request";
import def from "csi/def";
import Action from "csi/def/Action";
import FieldConstructor, { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import ViewRef from "csi/def/ViewRef";
import FormWidget from "csi/form/Widget";
import StoreNamesConstructor from "csi/store/Names";
import csiStore from "csi/store/_base";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import ProductType from "../ProductType";

const COLOR_SPECIFICATION = "ColorSpecification";
const COLOR_PALETTE = "ColorPalette";
const COLOR_IN_PALETTE = "ColorInPalette";
const PRODUCT_SOURCE = "ProductSource";

const ColorForm = declare(FormWidget, {

	rule: "ColorPaletteRuleTypes:Off",
	paletteField: null,

	/**
	 * @override
	 * Modified the store for Color Specification field based on selected palette
	 */
	getFieldStore: function (field, value) {
		const ctx = this.getContext();
		if (this.paletteField && this.paletteField.getValue() != csi.EMPTY_URL && field.getControlName() == COLOR_SPECIFICATION) {
			const search = new Search();
			const q = search.getQuery();
			q.RefAttr("__Parent__", xml.OP_EQUALS, this.paletteField.getValue())
				.Attr("Active", xml.OP_EQUALS, true);
			const qOr = q.Or();
			qOr.Node("Type", xml.OP_EQUALS, COLOR_SPECIFICATION);
			qOr.Node("Type", xml.OP_EQUALS, COLOR_IN_PALETTE);
			qOr.Node("Type", xml.OP_EQUALS, "PrintDesignColor");
			const rel = lang.mixin(new Relationship(), { m_target: "ProductColorSpec", m_query: q });
			const storeOptions = csiStore.namesOptionsByRelationship(ctx, rel);
			storeOptions.data = [csiStore.nameObject("")];
			storeOptions.action = lang.mixin(new Action(), {
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: this.rule == "ColorPaletteRuleTypes:Off" || !xml.isValidURL(this.paletteField.getValue()) ? "ProductColorSpecs" : "ColorInPalettes",
					m_url: "centric://APPAREL/Views",
				}),
				m_fixedQuery: q,
			});
			return new StoreNamesConstructor(storeOptions);
		}
		return this.inherited(arguments);
	},

	/**
	 * @override
	 * Add ColorPalette Field based on color rule
	 */
	_resolveView() {
		const formData = this.getFormData();
		const formAction = formData.getAction();
		const ac = this.defView.getSource();
		const parentProduct = this.isEdit() ? ac?.m_cache.getNode(this.formInfo?.parentUrl) : ac?.getParent();
		this.rule = parentProduct && ProductType.get(parentProduct, "ColorPaletteRule");
		if (this.rule == "ColorPaletteRuleTypes:Enforced" || this.rule == "ColorPaletteRuleTypes:Enabled") {
			this.paletteField = lang.mixin(new FieldConstructor(), {
				m_label: COLOR_PALETTE,
				m_typeControl: def.FIELD_FILTERING_SELECT,
				m_relationship: lang.mixin(new Relationship(), {
					m_typeNode: COLOR_PALETTE,
				}),
			});
			if (parentProduct.$Type === "Style") {
				const query = new Query();
				ProductType.applyStyleHierarchyFilters(parentProduct, query, ac.m_cache);
				this.paletteField.m_relationship.m_query = query;
			}
			if (this.rule == "ColorPaletteRuleTypes:Enforced") {
				this.paletteField.m_bRequired = true;
			}
			this.defView.addField(this.paletteField, COLOR_SPECIFICATION, null);
		}
		
		if (!this.isEdit() && parentProduct && parentProduct.$Type === "Material" && parentProduct.ColorAllocationStatus === "ColorAllocationStatus:Allocation WIP") {
			this.insertFieldIfNotExist(PRODUCT_SOURCE, 5, {
				m_typeControl: def.FIELD_MULTI_SELECT,
				m_relationship: lang.mixin(new Relationship(), {
					m_typeNode: PRODUCT_SOURCE,
					m_url: parentProduct.$URL,
					m_query: new Query().RefAttr("__Parent__", xml.OP_EQUALS, parentProduct.$URL, "Child:__Parent__"),
				}),
			});
		}

		if (formAction?.getId() == "NewFromColorPalette") {
			// on the NewFromColorPalette form, we should be basing off a new Colorway, not the ColorInPalette
			this.urlRoot = null;
		}

		this.inherited(arguments);
	},

	/**
	 * Set ColorSpecification store based on selected palette
	 * @param {View} defView
	 * @param {Field} field
	 * @param {Any} value
	 */
	_onChangePalette(defView, field, value) {
		this.resetFieldStoreAndValue(COLOR_SPECIFICATION, csi.EMPTY_URL);
		if (!this.isNewFrom()) {
			this.setFieldValue(this.defView.getField("ColorwayThemeColor"), csi.EMPTY_URL);
			this.setFieldDisabled(COLOR_SPECIFICATION, false);
		}
	},

	/**
	 * @override
	 * Add onchange event to the field on click
	 * ​For Edit and Copy, On the form pop up, the Color Palette and ColorSpecification
	 * fields should be populated based on the current values
	 */
	onViewEnd() {
		if (this.paletteField) {
			this.paletteField.m_onChange = "_onChangePalette";
		}
		return this.inherited(arguments);
	},

	/**
	 * @override
	 * Add dependency path for ColorSpecification and Theme
	 */
	_getRootRequest(node) {
		const req = this.inherited<Request>(arguments);
		if (req) {
			req.addDependencyPaths(["Child:ColorSpecification", "Child:__Parent__/Child:Theme"]);
		}
		return req;
	},

	/**
	 * @override
	 * Initialize Color Palette for edit and copy action
	 */
	onViewStartBuild() {
		const colorNode = this.getViewNode();
		const colorSpecNode = colorNode && this.cacheView.get(colorNode.ColorSpecification);
		const urlColor = this.getFixedValue("ColorSpecification");

		if (this.paletteField && colorSpecNode && colorSpecNode.$Type != "PrintDesignColor" && this.rule != "ColorPaletteRuleTypes:Off") {
			if (!this.isNewFrom()) {
				this.addDefaultValue(COLOR_PALETTE, colorSpecNode.__Parent__);
			} else {
				this.setFieldDisabled("ColorwayThemeColor", true);
			}
		} else if ((urlColor && this.isNewFrom()) || (colorSpecNode && colorSpecNode.$Type == "PrintDesignColor")){
			this.setFieldDisabled(COLOR_PALETTE, true);
		}
	},

});

export = ColorForm;
